// PMax Listing Groups — judgment row, Account Structure section.
// LLM-drafted verdict on whether asset-group product segmentation is coherent. This renderer
// supplies the per-asset-group structure table Claude saw, for verification.

import classNames from 'classnames';

import { count } from '../../utils/format';
import type { EvidenceProps } from './registry';

interface AssetGroupRow {
  campaignName: string;
  assetGroupName: string;
  includedCount: number;
  excludedCount: number;
  unsubdivided: boolean;
  dimensions: string[];
}

interface PmaxListingGroupsShape {
  totalAssetGroups: number;
  unsubdividedCount: number;
  assetGroups: AssetGroupRow[];
}

export const PmaxListingGroupsEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as PmaxListingGroupsShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Asset-group segmentation
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalAssetGroups} PMax asset group{e.totalAssetGroups === 1 ? '' : 's'}
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{count(e.totalAssetGroups)}</div>
            <div className='audience-coverage__lbl'>Asset groups</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{count(e.unsubdividedCount)}</div>
            <div className='audience-coverage__lbl'>Unsubdivided</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>Per asset group</h4>
        <table className='evidence__table evidence__table--listing-groups'>
          <thead>
            <tr>
              <th>Campaign › Asset group</th>
              <th>Structure</th>
            </tr>
          </thead>
          <tbody>
            {e.assetGroups.map(a => (
              <tr
                key={`${a.campaignName}-${a.assetGroupName}`}
                className={classNames({ 'evidence__row--flagged': a.unsubdivided })}
              >
                <td>
                  {a.campaignName}{' '}
                  <span className='evidence__inline-note'>› {a.assetGroupName}</span>
                </td>
                <td>
                  {a.unsubdivided ? (
                    <span className='evidence__strength evidence__strength--average'>
                      Unsubdivided (whole feed, 1 unit)
                    </span>
                  ) : (
                    <span>
                      Subdivided by {a.dimensions.join(', ') || '(unknown)'} · {a.includedCount}{' '}
                      included / {a.excludedCount} excluded
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        PMax listing-group filters live under each asset group and subdivide its products so
        creative + optimization can be matched to a product segment. A themed asset group left
        unsubdivided isn't matching its creative to a segment; excluded units are normal — that's
        how products are partitioned across asset groups.
      </p>
    </div>
  );
};
